import { useRecordContext } from "ra-core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LoaderCircle, LockKeyhole, LockKeyholeOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { Contact } from "../types";
import {
  fetchContactPaywall,
  setContactPaywall,
} from "../runtime-config/client";
import { RuntimeConfigRequestError } from "../runtime-config/contracts";

type PaywallChange = {
  whatsappId: string;
  override: boolean | null;
  expectedReleaseId: string;
};

function saveErrorMessage(error: unknown) {
  if (error instanceof RuntimeConfigRequestError) {
    if (error.code === "conflict")
      return "Configuration changed. Refresh the status, then try again.";
    if (error.code === "unauthorized")
      return "Your session expired. Sign in again to change the paywall.";
    if (error.code === "forbidden")
      return "Your account does not have permission to change the paywall.";
    if (error.status >= 400 && error.status < 500) {
      const codes = [error.message, error.details.reason].filter(
        (value): value is string =>
          typeof value === "string" && /^[a-z][a-z0-9_]{0,119}$/.test(value),
      );
      return `Paywall change was rejected${codes.length ? ` (${codes.join(": ")})` : ""}. Refresh the status before trying again.`;
    }
  }
  return "Could not confirm whether the paywall change was saved. Refresh the status before trying again.";
}

export const ContactPaywallToggle = () => {
  const contact = useRecordContext<Contact>();
  const whatsappId = contact?.whatsapp_user_id;
  const queryClient = useQueryClient();
  const queryKey = ["contact-paywall", whatsappId];
  const query = useQuery({
    queryKey,
    queryFn: () => fetchContactPaywall(whatsappId!),
    enabled: Boolean(whatsappId),
    retry: false,
    refetchInterval: (state) =>
      state.state.data?.applied === false ? 5000 : 30000,
  });
  const mutation = useMutation({
    onMutate: ({ whatsappId }: PaywallChange) =>
      queryClient.cancelQueries({ queryKey: ["contact-paywall", whatsappId] }),
    mutationFn: ({ whatsappId, override, expectedReleaseId }: PaywallChange) =>
      setContactPaywall(whatsappId, override, expectedReleaseId),
    onSuccess: (state, { whatsappId }) =>
      queryClient.setQueryData(["contact-paywall", whatsappId], state),
    onError: (_error, { whatsappId }) => {
      void queryClient.invalidateQueries({
        queryKey: ["contact-paywall", whatsappId],
      });
    },
  });

  if (!whatsappId)
    return (
      <p className="text-xs text-muted-foreground">
        Paywall requires a linked WhatsApp contact.
      </p>
    );
  if (!query.data) {
    return (
      <div className="text-xs text-muted-foreground">
        {query.isPending
          ? "Loading paywall status…"
          : "Paywall status unavailable."}
        {query.isError && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => void query.refetch()}
          >
            Retry
          </Button>
        )}
      </div>
    );
  }
  const state = query.data;
  const saveFailed =
    mutation.isError && mutation.variables?.whatsappId === whatsappId;
  const controlsDisabled =
    !state.can_edit ||
    mutation.isPending ||
    query.isError ||
    (saveFailed && query.isFetching);
  const changePaywall = (override: boolean | null) =>
    mutation.mutate({
      whatsappId,
      override,
      expectedReleaseId: state.release_id,
    });
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          aria-pressed={state.enabled}
          disabled={controlsDisabled}
          className={
            state.enabled
              ? "bg-emerald-700 text-white hover:bg-emerald-800"
              : "bg-slate-200 text-slate-900 hover:bg-slate-300 dark:bg-slate-700 dark:text-slate-100 dark:hover:bg-slate-600"
          }
          onClick={() => changePaywall(!state.enabled)}
        >
          {mutation.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : state.enabled ? (
            <LockKeyhole className="size-4" />
          ) : (
            <LockKeyholeOpen className="size-4" />
          )}
          {state.enabled ? "Disable paywall" : "Enable paywall"}
        </Button>
        <span className="text-xs" role="status">
          {query.isError ? "Last confirmed status: " : "Current status: "}
          {state.enabled ? "enabled" : "disabled"}
          {state.override === null ? " by default" : " for this contact"}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <span>Default: {state.default_enabled ? "enabled" : "disabled"}</span>
        {state.override !== null && (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            disabled={controlsDisabled}
            onClick={() => changePaywall(null)}
          >
            Use default
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {state.applied
          ? "Applies to the next B2C Echo. Existing Echoes keep their payment decision."
          : "Saved. Waiting for the bot to apply this change."}
      </p>
      {!state.can_edit && (
        <p className="text-xs text-muted-foreground">
          Configuration publisher access is required to change the paywall.
        </p>
      )}
      {(saveFailed || query.isError) && (
        <div className="space-y-1">
          <p role="alert" className="text-xs text-destructive">
            {query.isError
              ? "Could not refresh the paywall status. The status shown is the last confirmed value."
              : saveErrorMessage(mutation.error)}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={query.isFetching}
            onClick={async () => {
              const result = await query.refetch();
              if (result.isSuccess) mutation.reset();
            }}
          >
            Refresh status
          </Button>
        </div>
      )}
    </div>
  );
};
